import {
  lazy,
  Suspense,
  useEffect,
  useState,
  useCallback,
  type ComponentType,
  type LazyExoticComponent,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Header, Sidebar } from "@/components/layout";
import { AnnouncementBar } from "@/components/layout/AnnouncementBar";
import { Button, LoadingState } from "@/components/common";
import { useAuthStore } from "@/stores/authStore";
import { useTeamStore } from "@/stores/teamStore";
import { useUIStore } from "@/stores/uiStore";
import { useAssistantStore } from "@/stores/assistantStore";
import { isLeadershipTab } from "@/features/leadership-launchpad/leadership-paths";
import { AssistantPanel } from "@/components/assistant";
import { pagePath, tabForPath } from "@/navigation/page-registry";
import { canAccessAndrewOnlyTabs, isAndrewOnlyTab } from "@/navigation/access";
import * as klaviyoApi from "@/api/klaviyo";

interface MainLayoutProps {
  initialTab?: string;
}

type LazyNamedComponent<TModule, TExport extends keyof TModule> =
  TModule[TExport] extends ComponentType<infer TProps>
    ? LazyExoticComponent<ComponentType<TProps>>
    : never;

const DYNAMIC_IMPORT_RELOAD_KEY = "tc-insights-dynamic-import-reloaded";
const STALE_ASSET_RELOAD_KEY = "tc-insights-stale-asset-reload";

function isDynamicImportLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Expected a JavaScript-or-Wasm module script")
  );
}

async function clearStaleFrontendCaches() {
  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  if (!("caches" in window)) return;

  const cacheNames = await window.caches.keys();
  await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
}

function lazyNamed<TModule, TExport extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TExport,
): LazyNamedComponent<TModule, TExport> {
  return lazy(async () => {
    try {
      const module = await loader();
      sessionStorage.removeItem(DYNAMIC_IMPORT_RELOAD_KEY);
      sessionStorage.removeItem(STALE_ASSET_RELOAD_KEY);
      return { default: module[exportName] as ComponentType<unknown> };
    } catch (error) {
      const canReload =
        typeof window !== "undefined" &&
        isDynamicImportLoadError(error) &&
        sessionStorage.getItem(DYNAMIC_IMPORT_RELOAD_KEY) !== "1";

      if (canReload) {
        sessionStorage.setItem(DYNAMIC_IMPORT_RELOAD_KEY, "1");
        await clearStaleFrontendCaches();
        window.location.reload();
        return await new Promise<{ default: ComponentType<unknown> }>(() => undefined);
      }

      throw error;
    }
  }) as LazyNamedComponent<TModule, TExport>;
}

const DashboardTab = lazyNamed(
  () => import("@/features/dashboard"),
  "DashboardTab",
);
const ReviewsTab = lazyNamed(
  () => import("@/features/reviews/ReviewsTab"),
  "ReviewsTab",
);
const GorgiasTab = lazyNamed(() => import("@/features/gorgias"), "GorgiasTab");
const KodifTab = lazyNamed(() => import("@/features/kodif"), "KodifTab");
const FlipTab = lazyNamed(() => import("@/features/flip"), "FlipTab");
const TrustpilotTab = lazyNamed(
  () => import("@/features/trustpilot"),
  "TrustpilotTab",
);
const GoogleBusinessPage = lazyNamed(
  () => import("@/features/google-business"),
  "GoogleBusinessPage",
);
const PricingTab = lazyNamed(
  () => import("@/features/pricing/PricingTab"),
  "PricingTab",
);
const OOSTab = lazyNamed(() => import("@/features/oos/OOSTab"), "OOSTab");
const OperationsInventoryPanel = lazyNamed(
  () => import("@/features/operations/OperationsInventoryPanel"),
  "OperationsInventoryPanel",
);
const OosWorklistPanel = lazyNamed(
  () => import("@/features/operations/oos-worklist/OosWorklistPanel"),
  "OosWorklistPanel",
);
const SplitShipDashboardTab = lazyNamed(
  () => import("@/features/split-ship-dashboard"),
  "SplitShipDashboardTab",
);
const ProductionWipTab = lazyNamed(
  () => import("@/features/operations/ProductionWipTab"),
  "ProductionWipTab",
);
const ThreePLAuditTab = lazyNamed(
  () => import("@/features/threepl-audit"),
  "ThreePLAuditTab",
);
const OperationsCancellationsTab = lazyNamed(
  () => import("@/features/operations-cancellations"),
  "OperationsCancellationsTab",
);
const CloudinaryTab = lazyNamed(
  () => import("@/features/cloudinary/CloudinaryTab"),
  "CloudinaryTab",
);
const GtmTab = lazyNamed(() => import("@/features/gtm/GtmTab"), "GtmTab");
const BusinessCalendarTab = lazyNamed(
  () => import("@/features/business-calendar"),
  "BusinessCalendarTab",
);
const ForwardCoverage = lazyNamed(
  () => import("@/features/gtm/ForwardCoverage"),
  "ForwardCoverage",
);
const ReplenishmentDashboard = lazyNamed(
  () => import("@/features/gtm/ReplenishmentDashboard"),
  "ReplenishmentDashboard",
);
const WorkflowsTab = lazyNamed(
  () => import("@/features/workflows/WorkflowsTab"),
  "WorkflowsTab",
);
const RoadshowsTab = lazyNamed(
  () => import("@/features/wholesale/RoadshowsTab"),
  "RoadshowsTab",
);
const RepPerformanceTab = lazyNamed(
  () => import("@/features/wholesale/rep-performance/RepPerformanceTab"),
  "RepPerformanceTab",
);
const MilestonesTab = lazyNamed(
  () => import("@/features/wholesale/MilestonesTab"),
  "MilestonesTab",
);
const POGBuilderTab = lazyNamed(
  () => import("@/features/wholesale/POGBuilderTab"),
  "POGBuilderTab",
);
const ShipBobOrdersTab = lazyNamed(
  () => import("@/features/wholesale/ShipBobOrdersTab"),
  "ShipBobOrdersTab",
);
const MediaStudioTab = lazyNamed(
  () => import("@/features/media-studio"),
  "MediaStudioTab",
);
const EmailTab = lazyNamed(() => import("@/features/email"), "EmailTab");
const TeamPrioritiesTab = lazyNamed(
  () => import("@/features/team-priorities"),
  "TeamPrioritiesTab",
);
const TechStackTab = lazyNamed(
  () => import("@/features/tech-stack"),
  "TechStackTab",
);
const CommandCentreTab = lazyNamed(
  () => import("@/features/transformation/CommandCentreTab"),
  "CommandCentreTab",
);
const KnowledgeBaseTab = lazyNamed(
  () => import("@/features/knowledge-base"),
  "KnowledgeBaseTab",
);
const ExecDashboardsTab = lazyNamed(
  () => import("@/features/exec-dashboards"),
  "ExecDashboardsTab",
);
const NetSuiteMasterPlanTab = lazyNamed(
  () => import("@/features/netsuite-master-plan"),
  "NetSuiteMasterPlanTab",
);
const DailyRitualTab = lazyNamed(
  () => import("@/features/daily-ritual"),
  "DailyRitualTab",
);
const ExecPulseTab = lazyNamed(
  () => import("@/features/leadership-launchpad/ExecPulseTab"),
  "ExecPulseTab",
);
const BoardsTab = lazyNamed(
  () => import("@/features/leadership-launchpad/BoardsTab"),
  "BoardsTab",
);
const MyApprovalsTab = lazyNamed(
  () => import("@/features/approvals"),
  "MyApprovalsTab",
);
const CoachingTab = lazyNamed(
  () => import("@/features/leadership-launchpad/CoachingTab"),
  "CoachingTab",
);
const LearningTab = lazyNamed(
  () => import("@/features/leadership-launchpad/LearningTab"),
  "LearningTab",
);
const DogfoodingTab = lazyNamed(
  () => import("@/features/dogfooding"),
  "DogfoodingTab",
);
const GithubTab = lazyNamed(
  () => import("@/features/engineering"),
  "GithubTab",
);
const AgentsTab = lazyNamed(
  () => import("@/features/engineering"),
  "AgentsTab",
);
const SkillsTab = lazyNamed(
  () => import("@/features/engineering"),
  "SkillsTab",
);
const MillDeploymentsTab = lazyNamed(
  () => import("@/features/mill-deployments"),
  "MillDeploymentsTab",
);
const AiOsTab = lazyNamed(
  () => import("@/features/ai-os"),
  "AiOsTab",
);
const NotionAiTasksTab = lazyNamed(
  () => import("@/features/notion-ai-tasks"),
  "NotionAiTasksTab",
);
const ChargebacksTab = lazyNamed(
  () => import("@/features/chargebacks"),
  "ChargebacksTab",
);
const PricingOptimizerTab = lazyNamed(
  () => import("@/features/pricing-optimizer"),
  "PricingOptimizerTab",
);
const InventoryAuditTab = lazyNamed(
  () => import("@/features/inventory-audit"),
  "InventoryAuditTab",
);
const CXHomeTab = lazyNamed(() => import("@/features/cx-home"), "CXHomeTab");
const TargetTNATab = lazyNamed(
  () => import("@/features/product"),
  "TargetTNATab",
);

export function MainLayout({ initialTab = "dashboard" }: MainLayoutProps) {
  const klaviyoHeartbeatEnabled =
    import.meta.env.VITE_ENABLE_KLAVIYO_UI_HEARTBEAT === "true";
  const klaviyoHeartbeatMinutes = Number.parseInt(
    import.meta.env.VITE_KLAVIYO_UI_HEARTBEAT_MINUTES ?? "30",
    10,
  );
  const klaviyoHeartbeatIntervalMs =
    Number.isFinite(klaviyoHeartbeatMinutes) && klaviyoHeartbeatMinutes > 0
      ? klaviyoHeartbeatMinutes * 60 * 1000
      : 30 * 60 * 1000;

  const navigate = useNavigate();
  const location = useLocation();
  const teams = useAuthStore((state) => state.teams);
  const currentUserEmail = useAuthStore((state) => state.user?.email);
  const isTeamsLoading = useAuthStore((state) => state.isTeamsLoading);
  const currentTeam = useTeamStore((state) => state.currentTeam);
  const setCurrentTeam = useTeamStore((state) => state.setCurrentTeam);
  const activeTab = useUIStore((state) => state.activeTab);
  const setActiveTab = useUIStore((state) => state.setActiveTab);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const updateAssistantContext = useAssistantStore(
    (state) => state.updateContext,
  );
  const canViewAndrewOnlyTabs = canAccessAndrewOnlyTabs(currentUserEmail);

  // Auto-select team if only one available, or refresh current team with full data
  const isAuthLoading = useAuthStore((state) => state.isLoading);
  useEffect(() => {
    if (teams.length === 0) {
      // Don't clear persisted team while auth or teams are still loading
      if (isTeamsLoading || isAuthLoading) return;
      if (currentTeam) setCurrentTeam(null);
      return;
    }

    if (currentTeam) {
      // Validate persisted team still exists in API response
      const freshTeam = teams.find((t) => t.id === currentTeam.id);
      if (!freshTeam) {
        // Persisted team no longer exists (e.g. DB was reset) - clear it
        setCurrentTeam(teams.length === 1 ? teams[0] : null);
      } else if (!currentTeam.created_at) {
        // Refresh current team with full data from API (persisted team only has id/name)
        setCurrentTeam(freshTeam);
      }
    } else if (teams.length === 1) {
      // Auto-select only team
      setCurrentTeam(teams[0]);
    }
  }, [teams, currentTeam, setCurrentTeam, isTeamsLoading, isAuthLoading]);

  // Set initial tab from route
  useEffect(() => {
    if (initialTab && initialTab !== activeTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab, activeTab, setActiveTab]);

  useEffect(() => {
    if (!canViewAndrewOnlyTabs && isAndrewOnlyTab(activeTab)) {
      setActiveTab("dashboard");
      navigate("/", { replace: true });
    }
  }, [activeTab, canViewAndrewOnlyTabs, navigate, setActiveTab]);

  useEffect(() => {
    const routeTab = tabForPath(location.pathname);
    if (routeTab) setActiveTab(routeTab);
  }, [location.pathname, setActiveTab]);

  useEffect(() => {
    const mainPane = document.getElementById("dashboardScreen");
    mainPane?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  useEffect(() => {
    updateAssistantContext({ activeTab, currentPage: activeTab });
  }, [activeTab, updateAssistantContext]);

  const handleTabChange = (tab: string) => {
    if (!canViewAndrewOnlyTabs && isAndrewOnlyTab(tab)) {
      setActiveTab("dashboard");
      navigate("/", { replace: true });
      return;
    }
    setActiveTab(tab);
    if (tab === "dashboard") {
      navigate("/");
    } else if (pagePath(tab)) {
      navigate(pagePath(tab)!);
    } else if (isLeadershipTab(tab)) {
      navigate(`/leadership/${tab}`);
    } else if (tab === "github") {
      navigate("/engineering/github");
    } else if (tab === "skills") {
      navigate("/engineering/skills");
    } else if (tab === "shipbob-orders") {
      navigate("/wholesale/order-tracker/dashboard");
    } else {
      navigate(`/${tab}`);
    }
  };

  // Memoized callback to prevent ReviewsTab from refetching on every render
  const handleDataLoaded = useCallback(() => {
    setLastUpdated(new Date());
  }, []);

  // While logged in, periodically trigger lightweight Klaviyo reads.
  // Backend uses these requests to kick off stale background syncs automatically.
  useEffect(() => {
    if (
      !klaviyoHeartbeatEnabled ||
      !currentTeam?.id ||
      !currentTeam.klaviyo_configured
    )
      return;

    let cancelled = false;

    const triggerKlaviyoHeartbeat = async () => {
      if (cancelled) return;
      try {
        await Promise.all([
          klaviyoApi.listKlaviyoCampaigns(currentTeam.id, 1),
          klaviyoApi.listKlaviyoFlows(currentTeam.id, 1),
        ]);
      } catch {
        // Non-blocking background heartbeat.
      }
    };

    void triggerKlaviyoHeartbeat();
    const intervalId = window.setInterval(() => {
      void triggerKlaviyoHeartbeat();
    }, klaviyoHeartbeatIntervalMs);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    currentTeam?.id,
    currentTeam?.klaviyo_configured,
    klaviyoHeartbeatEnabled,
    klaviyoHeartbeatIntervalMs,
  ]);

  // Show loading until teams are fetched to avoid auth flicker
  if (!currentTeam && isTeamsLoading) {
    return (
      <div className="app-container active">
        <AnnouncementBar />
        <Header />
        <div className="app-body">
          <Sidebar activeTab={activeTab} onTabChange={handleTabChange} />
          <div className="dashboard-container">
            <LoadingState message="Loading your teams..." />
          </div>
          <AssistantPanel />
        </div>
      </div>
    );
  }

  // Show setup screen if no team selected
  if (!currentTeam) {
    return (
      <div className="app-container active">
        <AnnouncementBar />
        <Header />
        <div
          id="setupScreen"
          className="setup-container"
          style={{ display: "flex" }}
        >
          <div className="setup-card">
            <h2 className="setup-title">Welcome to The Mill</h2>
            <p className="setup-description">
              {teams.length === 0
                ? "You need to be part of a team to get started. Please contact your team admin or go to Settings to create a new team."
                : "Select a team from the dropdown above to get started."}
            </p>
            {teams.length === 0 && (
              <Button className="w-full" onClick={() => navigate("/settings")}>
                Go to Settings
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="appContainer" className="app-container active">
      <AnnouncementBar />
      <Header lastUpdated={lastUpdated} />

      <div className="app-body">
        <Sidebar activeTab={activeTab} onTabChange={handleTabChange} />

        <div id="dashboardScreen" className="dashboard-container">
          <Suspense fallback={<LoadingState message="Loading workspace..." />}>
            {activeTab === "dashboard" && <DashboardTab />}
            {activeTab === "okendo" && (
              <ReviewsTab onDataLoaded={handleDataLoaded} />
            )}

            {activeTab === "gorgias" && <GorgiasTab />}

            {activeTab === "kodif" && <KodifTab />}

            {activeTab === "flip" && <FlipTab />}

            {activeTab === "trustpilot" && <TrustpilotTab />}

            {activeTab === "google-business" && <GoogleBusinessPage />}

            {activeTab === "pricing" && <PricingTab />}

            {activeTab === "fulfillment" && <OOSTab />}
            {activeTab === "3pl-audit" && <ThreePLAuditTab />}

            {activeTab === "operations-inventory" && (
              <OperationsInventoryPanel />
            )}
            {activeTab === "operations-oos-queue" && <OosWorklistPanel />}
            {activeTab === "split-ship" && <SplitShipDashboardTab />}
            {activeTab === "operations-cancellations" && (
              <OperationsCancellationsTab />
            )}
            {activeTab === "production-wip" && <ProductionWipTab />}

            {activeTab === "cloudinary" && <CloudinaryTab />}
            {activeTab === "gtm" && <GtmTab />}
            {activeTab === "calendar" && <BusinessCalendarTab />}
            {activeTab === "forward-coverage" && <ForwardCoverage />}
            {activeTab === "replenishment" && <ReplenishmentDashboard />}

            {activeTab === "workflows" && <WorkflowsTab />}

            {activeTab === "milestones" && <MilestonesTab />}

            {activeTab === "roadshows" && <RoadshowsTab />}
            {activeTab === "roadshow-rep-performance" && <RepPerformanceTab />}

            {activeTab === "planograms" && <POGBuilderTab />}

            {activeTab === "shipbob-orders" && <ShipBobOrdersTab />}

            {(activeTab === "studio" ||
              activeTab === "media-studio" ||
              activeTab === "video-studio" ||
              activeTab === "image-studio") && <MediaStudioTab />}

            {(activeTab === "email" || activeTab === "email-studio") && (
              <EmailTab />
            )}

            {activeTab === "team-priorities" && <TeamPrioritiesTab />}
            {activeTab === "exec-pulse" && <ExecPulseTab />}
            {activeTab === "exec-dashboards" && <ExecDashboardsTab />}
            {activeTab === "boards" && <BoardsTab />}
            {activeTab === "approvals" && <MyApprovalsTab />}
            {activeTab === "coaching" && <CoachingTab />}
            {activeTab === "learning" && <LearningTab />}
            {activeTab === "tech-stack" && <TechStackTab />}
            {activeTab === "knowledge-base" && <KnowledgeBaseTab />}
            {activeTab === "netsuite-master-plan" && <NetSuiteMasterPlanTab />}
            {activeTab === "daily-ritual" && <DailyRitualTab />}
            {activeTab === "dogfooding" && <DogfoodingTab />}

            {activeTab === "chargebacks" && <ChargebacksTab />}
            {activeTab === "pricing-optimizer" && <PricingOptimizerTab />}
            {activeTab === "inventory-audit" && <InventoryAuditTab />}

            {activeTab === "cx-home" && <CXHomeTab />}

            {activeTab === "target-tna" && <TargetTNATab />}

            {activeTab === "github" && <GithubTab />}
            {activeTab === "agents" && <AgentsTab />}
            {activeTab === "skills" && <SkillsTab />}
            {activeTab === "mill-deployments" && <MillDeploymentsTab />}
            {activeTab === "ai-os" && <AiOsTab />}
            {activeTab === "notion-ai-tasks" && <NotionAiTasksTab />}
            {activeTab === "transformation-dashboard" && <CommandCentreTab />}

          </Suspense>
        </div>

        <AssistantPanel />
      </div>
    </div>
  );
}

export default MainLayout;
